// Copyright 2026 Sorama B.V.

import { GasType } from "datatypes/SorXMeasurement";
import { MassCost } from "units/Mass";
import { MassFlow } from "units/MassFlow";
import { Unit } from "units/Unit";
import { VolumeCost } from "units/Volume";
import { VolumeFlow } from "units/VolumeFlow";

/**
 * Custom unit converters for cross-family conversions that require context
 */
export class UnitConverters {
    private static gasDensities: Record<GasType, number> = {
        Air: 1.205,
        Argon: 1.661,
        Helium: 0.1664,
        Hydrogen: 0.0899,
        Nitrogen: 1.165,
        "Natural Gas": 0.7,
        Methane: 0.668,
        Oxygen: 1.331,
        CO2: 1.842,
        Ammonia: 0.717,
        Butane: 2.489,
        Propane: 1.882,
        Unknown: 1.205,
        Other: 1.205,
    };

    public static getGasDensity(gasType: GasType): number {
        const density = this.gasDensities[gasType];
        if (density === undefined) {
            throw new Error(`Invalid gas type: ${gasType}`);
        }
        return density;
    }

    /**
     * Convert from VolumeFlow (L/min) to MassFlow (g/hour)
     */
    public static convertVolumeFlowToMassFlow(
        sourceUnit: Unit,
        targetUnit: Unit,
        value: number,
        context?: { gasType?: GasType }
    ): number {
        const gasType = context?.gasType || "Air";
        const density = UnitConverters.getGasDensity(gasType);

        // Convert source to L/min
        const lpm = VolumeFlow.litersPerMinute.fromUnit(sourceUnit, value);

        // Convert L/min to g/hour using density
        const gph = lpm * 60 * density;

        // Convert to target MassFlow unit
        return targetUnit.fromUnit(MassFlow.gramsPerHour, gph);
    }

    /**
     * Convert from MassFlow (g/hour) to VolumeFlow (L/min)
     */
    public static convertMassFlowToVolumeFlow(
        sourceUnit: Unit,
        targetUnit: Unit,
        value: number,
        context?: { gasType?: GasType }
    ): number {
        const gasType = context?.gasType || "Air";
        const density = UnitConverters.getGasDensity(gasType);

        // Convert source to g/hour
        const gph = MassFlow.gramsPerHour.fromUnit(sourceUnit, value);

        // Convert g/hour to L/min using density
        const lpm = gph / (60 * density);

        // Convert to target VolumeFlow unit
        return targetUnit.fromUnit(VolumeFlow.litersPerMinute, lpm);
    }

    /**
     * Convert from VolumeCost (e.g. EUR/L) to MassCost (e.g. EUR/g)
     * Preserves the numeric value since gas cost is user-entered and
     * a meaningful volume↔mass price conversion would require gas density context.
     */
    public static convertVolumeCostToMassCost(_sourceUnit: Unit, _targetUnit: Unit, value: number): number {
        return value;
    }

    /**
     * Convert from MassCost (e.g. EUR/g) to VolumeCost (e.g. EUR/L)
     * Preserves the numeric value since gas cost is user-entered and
     * a meaningful mass↔volume price conversion would require gas density context.
     */
    public static convertMassCostToVolumeCost(_sourceUnit: Unit, _targetUnit: Unit, value: number): number {
        return value;
    }

    /**
     * Register all custom converters with the Unit system
     */
    public static registerConverters(): void {
        // Register VolumeFlow → MassFlow converter
        Unit.registerCustomConverter(
            VolumeFlow.typeName,
            MassFlow.typeName,
            UnitConverters.convertVolumeFlowToMassFlow
        );

        // Register MassFlow → VolumeFlow converter
        Unit.registerCustomConverter(
            MassFlow.typeName,
            VolumeFlow.typeName,
            UnitConverters.convertMassFlowToVolumeFlow
        );

        // Register VolumeCost → MassCost converter
        Unit.registerCustomConverter(
            VolumeCost.typeName,
            MassCost.typeName,
            UnitConverters.convertVolumeCostToMassCost
        );

        // Register MassCost → VolumeCost converter
        Unit.registerCustomConverter(
            MassCost.typeName,
            VolumeCost.typeName,
            UnitConverters.convertMassCostToVolumeCost
        );
    }
}
